import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Plus, Upload, FileSpreadsheet, FileText, Eye, Pencil, AlertTriangle, Image as ImageIcon, ImageOff, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { EvidenciaStatusBadge } from "@/components/EvidenciaStatusBadge";
import { EsiStatusBadge } from "@/components/EsiStatusBadge";
import { useStatusOptions } from "@/hooks/useStatusOptions";
import { useEvidenciaStatusOptions } from "@/hooks/useEvidenciaStatusOptions";
import { useEsiStatusOptions } from "@/hooks/useEsiStatusOptions";
import { usePermission } from "@/hooks/usePermission";
import { toast } from "sonner";

export default function OTsListPage() {
  const [search, setSearch] = useState("");
  const [estatus, setEstatus] = useState("all");
  const [estatusEvidencia, setEstatusEvidencia] = useState("all");
  const [estatusEsi, setEstatusEsi] = useState("all");
  const [estatusCarga, setEstatusCarga] = useState("all");
  const [soloFaltante, setSoloFaltante] = useState(false);
  const { options: STATUS_OPTIONS } = useStatusOptions();
  const { options: EVIDENCIA_STATUS_OPTIONS } = useEvidenciaStatusOptions();
  const { options: ESI_STATUS_OPTIONS } = useEsiStatusOptions();
  const canCreate = usePermission("ot_create");
  const canUpload = usePermission("ot_upload");
  const canEdit = usePermission("ot_edit");
  const canExport = usePermission("ot_export");

  const { data: ots = [], isLoading, refetch } = useQuery({
    queryKey: ["ots-list", search, soloFaltante],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (soloFaltante) params.solo_faltante = true;
      return (await api.get("/ots", { params: Object.keys(params).length ? params : undefined })).data;
    },
  });

  const filtered = useMemo(() => {
    return ots.filter((o) => {
      if (estatus !== "all" && o.estatus !== estatus) return false;
      if (estatusEvidencia !== "all" && (o.estatus_evidencia || "Pendiente de subir") !== estatusEvidencia) return false;
      if (estatusEsi !== "all" && (o.estatus_esi || "Pendiente revisión") !== estatusEsi) return false;
      return true;
    });
  }, [ots, estatus, estatusEvidencia, estatusEsi]);

  const downloadFile = async (id, kind, folio) => {
    try {
      const res = await api.get(`/ots/${id}/${kind}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OT_${folio}.${kind === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Descarga ${kind.toUpperCase()} iniciada`);
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Órdenes de Trabajo</h1>
          <p className="text-sm text-muted-foreground">Listado completo de OTs con filtros y descargas.</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2">
          {canUpload && <Link to="/ots/upload" className="col-span-1"><Button variant="secondary" size="sm" className="w-full sm:w-auto h-11 sm:h-9" data-testid="ots-upload-button"><Upload className="h-4 w-4 mr-2" />Cargar Excel</Button></Link>}
          {canCreate && <Link to="/ots/new" className="col-span-1"><Button size="sm" className="w-full sm:w-auto h-11 sm:h-9" data-testid="ots-new-button"><Plus className="h-4 w-4 mr-2" />Nueva OT</Button></Link>}
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="ots-search-input"
                placeholder="Buscar por folio, cliente, PO, SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-11 sm:h-10"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap lg:flex-nowrap">
              <Select value={estatus} onValueChange={setEstatus}>
                <SelectTrigger className="flex-1 lg:flex-none lg:w-44 min-w-[130px] h-11 sm:h-10" data-testid="ots-status-select">
                  <SelectValue placeholder="Estatus" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estatus</SelectItem>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={estatusEvidencia} onValueChange={setEstatusEvidencia}>
                <SelectTrigger className="flex-1 lg:flex-none lg:w-52 min-w-[150px] h-11 sm:h-10" data-testid="ots-estatus-evidencia-select">
                  <SelectValue placeholder="Estatus Evidencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toda evidencia</SelectItem>
                  {EVIDENCIA_STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={estatusEsi} onValueChange={setEstatusEsi}>
                <SelectTrigger className="flex-1 lg:flex-none lg:w-52 min-w-[150px] h-11 sm:h-10" data-testid="ots-estatus-esi-select">
                  <SelectValue placeholder="Estatus ESI" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo ESI</SelectItem>
                  {ESI_STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className={`flex items-center gap-2 px-3 h-11 sm:h-10 rounded-md border transition-colors shrink-0 ${soloFaltante ? "border-destructive bg-destructive/10" : "border-border"}`}>
                <Switch
                  id="solo-faltante"
                  checked={soloFaltante}
                  onCheckedChange={setSoloFaltante}
                  data-testid="ots-solo-faltante-switch"
                />
                <Label htmlFor="solo-faltante" className="text-xs sm:text-sm cursor-pointer whitespace-nowrap">
                  Solo faltante
                </Label>
              </div>
              <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-11 sm:h-10 hidden sm:inline-flex">Actualizar</Button>
            </div>
          </div>

          {filtered.length > 0 && (
            <div className="lg:hidden text-[10px] text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
              <span aria-hidden>←</span>
              <span>Desliza horizontalmente para ver más columnas</span>
              <span aria-hidden>→</span>
            </div>
          )}

          <div className="overflow-x-auto rounded-md border border-border -mx-3 sm:mx-0" data-testid="ots-table-scroll">
            <table className="w-full text-sm min-w-[1180px]">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-3">Folio</th>
                  <th className="py-2 px-3">Cliente</th>
                  <th className="py-2 px-3">Solicitante</th>
                  <th className="py-2 px-3">Puerto</th>
                  <th className="py-2 px-3">Centro</th>
                  <th className="py-2 px-3">Pedimento</th>
                  <th className="py-2 px-3">Arribo</th>
                  <th className="py-2 px-3 text-right">SKUs</th>
                  <th className="py-2 px-3 text-right">PZ</th>
                  <th className="py-2 px-3 text-right">Faltante</th>
                  <th className="py-2 px-3 text-right">Sobrante</th>
                  <th className="py-2 px-3">Estatus</th>
                  <th className="py-2 px-3">Est. Evidencia</th>
                  <th className="py-2 px-3">Ev.</th>
                  <th className="py-2 px-3">Est. ESI</th>
                  <th className="py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody data-testid="ots-table-body">
                {isLoading && (
                  <tr><td colSpan={15} className="text-center py-10 text-muted-foreground">Cargando...</td></tr>
                )}
                {!isLoading && filtered.map((o) => {
                  const pedimentos = Array.from(new Set((o.items || []).map((it) => it.pedimento).filter(Boolean)));
                  const pedimentoLabel = pedimentos.length === 0
                    ? "-"
                    : pedimentos.length === 1
                      ? pedimentos[0]
                      : `${pedimentos[0]} +${pedimentos.length - 1}`;
                  const totalFaltante = o.total_faltante || 0;
                  const totalSobrante = o.total_sobrante || 0;
                  const evStatus = o.estatus_evidencia || "Pendiente de subir";
                  const noRequiereEv = evStatus === "No requiere evidencia";
                  return (
                  <tr key={o.id} className={`border-t border-border/60 hover:bg-muted/30 ${totalFaltante > 0 ? "bg-destructive/5" : totalSobrante > 0 ? "bg-[hsl(38_92%_55%/0.06)]" : ""}`}>
                    <td className="py-2 px-3 font-mono font-medium"><Link to={`/ots/${o.id}`} className="text-primary hover:underline" data-testid={`ot-row-folio-${o.folio}`}>{o.folio}</Link></td>
                    <td className="py-2 px-3">{o.cliente}</td>
                    <td className="py-2 px-3 text-muted-foreground">{o.solicitante}</td>
                    <td className="py-2 px-3 text-muted-foreground">{o.puerto_entrada || "-"}</td>
                    <td className="py-2 px-3 text-muted-foreground">{o.centro || "-"}</td>
                    <td className="py-2 px-3 text-muted-foreground font-mono text-xs" title={pedimentos.join(", ")} data-testid={`ot-pedimento-${o.folio}`}>{pedimentoLabel}</td>
                    <td className="py-2 px-3 text-muted-foreground font-mono text-xs">{(o.fecha_arribo || "-").slice(0, 10)}</td>
                    <td className="py-2 px-3 text-right font-mono">{o.unique_skus || 0}</td>
                    <td className="py-2 px-3 text-right font-mono">{o.total_pz || 0}</td>
                    <td className={`py-2 px-3 text-right font-mono ${totalFaltante > 0 ? "text-destructive font-bold" : "text-muted-foreground"}`} data-testid={`ot-faltante-${o.folio}`}>
                      {totalFaltante > 0 ? `⚠ ${totalFaltante}` : "0"}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${totalSobrante > 0 ? "text-[hsl(var(--status-pending))] font-bold" : "text-muted-foreground"}`} data-testid={`ot-sobrante-${o.folio}`}>
                      {totalSobrante > 0 ? `⚠ ${totalSobrante}` : "0"}
                    </td>
                    <td className="py-2 px-3"><StatusBadge status={o.estatus} /></td>
                    <td className="py-2 px-3" data-testid={`ot-evidencia-status-${o.folio}`}>
                      <EvidenciaStatusBadge status={evStatus} />
                    </td>
                    <td className="py-2 px-3" data-testid={`ot-evidencia-count-${o.folio}`}>
                      {(o.evidencia_count ?? 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 font-mono text-xs" title={`${o.evidencia_count} evidencia(s) cargada(s)`}>
                          <ImageIcon className="h-3.5 w-3.5" />
                          <span className="tabular-nums">{o.evidencia_count}</span>
                        </span>
                      ) : noRequiereEv ? (
                        <span
                          className="inline-flex items-center gap-1 text-emerald-400 font-mono text-xs"
                          title="Esta OT no tiene piezas dañadas (C+D=0), no se requiere evidencia."
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span>OK</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground/60 text-xs" title="Sin evidencia cargada">
                          <ImageOff className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3" data-testid={`ot-esi-status-${o.folio}`}>
                      <EsiStatusBadge status={o.estatus_esi || "Pendiente revisión"} />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <Link to={`/ots/${o.id}`}>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Ver" data-testid={`ot-view-${o.folio}`}><Eye className="h-4 w-4" /></Button>
                        </Link>
                        {canEdit && (
                          <Link to={`/ots/${o.id}/edit`}>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar" data-testid={`ot-edit-${o.folio}`}><Pencil className="h-4 w-4 text-amber-400" /></Button>
                          </Link>
                        )}
                        {canExport && (
                          <>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Excel" data-testid={`ot-excel-${o.folio}`} onClick={() => downloadFile(o.id, "excel", o.folio)}><FileSpreadsheet className="h-4 w-4 text-emerald-400" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="PDF" data-testid={`ot-pdf-${o.folio}`} onClick={() => downloadFile(o.id, "pdf", o.folio)}><FileText className="h-4 w-4 text-rose-400" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );})}
                {!isLoading && filtered.length === 0 && (
                  <tr><td colSpan={15} className="text-center py-12 text-muted-foreground text-sm">No hay OTs que coincidan con los filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <span data-testid="ots-list-count">{filtered.length} OTs</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
